import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    checkPaymentStatus(sessionId);
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, currentAttempt = 0) => {
    const maxAttempts = 5;
    
    try {
      const response = await axios.get(
        `${API}/payments/checkout-status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else if (currentAttempt < maxAttempts) {
        setTimeout(() => {
          setAttempts(currentAttempt + 1);
          checkPaymentStatus(sessionId, currentAttempt + 1);
        }, 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-6"></div>
          <h2 className="text-2xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Confirming Payment
          </h2>
          <p className="text-muted-foreground">Please wait while we verify your subscription...</p>
          <p className="text-sm text-muted-foreground mt-2">Attempt {attempts + 1} of 5</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="text-3xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Welcome to Rooted!
          </h2>
          <p className="text-muted-foreground mb-8">
            Your subscription is now active. Begin your 365-day devotional journey today.
          </p>
          <Button
            data-testid="go-to-dashboard-btn"
            className="rounded-full px-8"
            onClick={() => navigate('/dashboard')}
          >
            Start Your Journey
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <XCircle className="w-20 h-20 text-red-600 mx-auto mb-6" strokeWidth={1.5} />
        <h2 className="text-3xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Payment Issue
        </h2>
        <p className="text-muted-foreground mb-8">
          {status === 'expired'
            ? 'Your payment session has expired.'
            : status === 'timeout'
            ? 'Payment verification timed out. Please check your email for confirmation.'
            : 'There was an error processing your payment.'}
        </p>
        <Button
          data-testid="try-again-btn"
          className="rounded-full px-8"
          onClick={() => navigate('/subscribe')}
        >
          Try Again
        </Button>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;